import { Carousel } from '@mantine/carousel'
import {
  Alert,
  AspectRatio,
  Box,
  Image,
  LoadingOverlay,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import type { EmblaCarouselType } from 'embla-carousel'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbAlertTriangle } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

import { getUnexpectedEventCameraCapturesOptions } from '@/api/main/@tanstack/react-query.gen'
import { parsePhotoTimestamp } from '@/utils/photoTimestamp'

import PhotoLightbox from './PhotoLightbox'

type Props = {
  eventId: string
}

const UnexpectedEventPhotos = ({ eventId }: Props) => {
  const { t } = useTranslation('unexpectedEvents')
  const theme = useMantineTheme()
  const isDark = useComputedColorScheme('light') === 'dark'
  const slideBg = isDark ? theme.colors.dark[6] : theme.colors.gray[1]
  const query = useQuery(getUnexpectedEventCameraCapturesOptions({ path: { event_id: eventId } }))

  const photos = useMemo(() => {
    const captures = query.data?.captures ?? []
    return captures.map(capture => {
      const content = capture.content as unknown as string
      const src = `data:image/jpeg;base64,${content}`
      const parsed = parsePhotoTimestamp(capture.name)
      return { name: capture.name, src, caption: parsed.label }
    })
  }, [query.data])

  // The carousel should open at the middle photo — that's the moment the
  // unexpected event was raised.
  const initialSlide = photos.length > 0 ? Math.floor(photos.length / 2) : 0

  const emblaRef = useRef<EmblaCarouselType | null>(null)
  const [activeIndex, setActiveIndex] = useState(initialSlide)
  const [lightboxOpened, { open: openLightbox, close: closeLightbox }] = useDisclosure(false)

  // Reset the active index whenever the photo set changes (e.g. event switch).
  useEffect(() => {
    setActiveIndex(initialSlide)
  }, [initialSlide])

  // Keep the carousel in sync when the lightbox is used to navigate.
  const handleLightboxIndexChange = (index: number) => {
    setActiveIndex(index)
    emblaRef.current?.scrollTo(index)
  }

  if (query.isLoading) {
    return (
      <AspectRatio ratio={16 / 9} bg={slideBg} style={{ position: 'relative' }}>
        <LoadingOverlay visible />
      </AspectRatio>
    )
  }

  if (query.isError) {
    return (
      <Alert color="red" icon={<TbAlertTriangle />}>
        {t('photos.loadError')}
      </Alert>
    )
  }

  if (photos.length === 0) {
    return (
      <Alert color="gray" variant="light">
        {t('photos.noPhotos')}
      </Alert>
    )
  }

  return (
    <>
      <Stack gap="xs">
        <Carousel
          withIndicators={photos.length > 1}
          withControls={photos.length > 1}
          slideSize="100%"
          slideGap="xs"
          height={420}
          initialSlide={initialSlide}
          getEmblaApi={api => {
            emblaRef.current = api
          }}
          onSlideChange={setActiveIndex}
          emblaOptions={{ loop: false, align: 'center' }}
          aria-label={t('panels.photos')}
        >
          {photos.map(photo => (
            <Carousel.Slide key={photo.name}>
              <Box
                onClick={openLightbox}
                role="button"
                aria-label={t('photos.openLightbox')}
                style={{
                  cursor: 'zoom-in',
                  width: '100%',
                  height: '100%',
                  background: slideBg,
                }}
              >
                <Image src={photo.src} alt={photo.name} fit="contain" h="100%" w="100%" />
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>

        <Text size="xs" c="dimmed" ta="center" truncate="end" title={photos[activeIndex]?.caption}>
          {t('photos.photoOf', { current: activeIndex + 1, total: photos.length })}
          {photos[activeIndex]?.caption ? ` — ${photos[activeIndex].caption}` : ''}
        </Text>
      </Stack>

      <PhotoLightbox
        opened={lightboxOpened}
        onClose={closeLightbox}
        photos={photos}
        index={activeIndex}
        onIndexChange={handleLightboxIndexChange}
      />
    </>
  )
}

export default UnexpectedEventPhotos
